import { Head, Link, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Activity {
  id: number
  title: string
}
interface Option {
  id: number
  name: string
}
interface Props {
  activity: Activity
  client_types: Option[]
  sales_reps: Option[]
  contact_roles: Option[]
  log_types: Option[]
  employees: Option[]
  errors?: Record<string, string[]>
}

export default function QuickAdd({
  activity,
  client_types,
  sales_reps,
  contact_roles,
  log_types,
  employees,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Marketing Activities", href: "/marketing_activities" },
    { title: activity.title, href: `/marketing_activities/${activity.id}` },
    {
      title: "Attendees",
      href: `/marketing_activities/${activity.id}/list_items`,
    },
    {
      title: "Quick Add",
      href: `/marketing_activities/${activity.id}/list_items/quick_add`,
    },
  ]

  const { data, setData, post, processing } = useForm({
    customer: {
      company_name: "",
      client_type_id: "",
      sales_rep_id: "",
      address: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
      office_phone: "",
      email: "",
      website: "",
      notes: "",
    },
    contact: {
      salutation: "",
      first_name: "",
      middle_name: "",
      last_name: "",
      suffix: "",
      department: "",
      role: "",
      phone: "",
      cell_phone: "",
      email: "",
    },
    follow_up: {
      scheduled_date: "",
      log_type_id: "",
      user_id: "",
      scheduled_notes: "",
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/marketing_activities/${activity.id}/list_items/quick_add`)
  }

  const setCustomerField = (field: string, value: string) => {
    setData("customer", { ...data.customer, [field]: value })
  }

  const setContactField = (field: string, value: string) => {
    setData("contact", { ...data.contact, [field]: value })
  }

  const setFollowUpField = (field: string, value: string) => {
    setData("follow_up", { ...data.follow_up, [field]: value })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Quick Add Customer & Contact" />
      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">
          Quick Add Customer &amp; Contact to {activity.title}
        </h1>
        <form onSubmit={handleSubmit} className="max-w-3xl space-y-8">
          {/* Company Section */}
          <fieldset className="border-hpi-light-gray space-y-4 rounded border p-4">
            <legend className="text-hpi-dark-blue px-2 font-semibold">
              Company
            </legend>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium">
                  Company Name <span className="text-hpi-red">*</span>
                </label>
                <input
                  type="text"
                  value={data.customer.company_name}
                  onChange={(e) =>
                    setCustomerField("company_name", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={100}
                  required
                />
                {errors?.company_name && (
                  <p className="text-hpi-red text-sm">
                    {errors.company_name.join(", ")}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Company Type
                </label>
                <select
                  value={data.customer.client_type_id}
                  onChange={(e) =>
                    setCustomerField("client_type_id", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                >
                  <option value="">Select...</option>
                  {client_types.map((ct) => (
                    <option key={ct.id} value={ct.id}>
                      {ct.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium">Rep</label>
                <select
                  value={data.customer.sales_rep_id}
                  onChange={(e) =>
                    setCustomerField("sales_rep_id", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                >
                  <option value="">Select...</option>
                  {sales_reps.map((rep) => (
                    <option key={rep.id} value={rep.id}>
                      {rep.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium">Address</label>
                <input
                  type="text"
                  value={data.customer.address}
                  onChange={(e) => setCustomerField("address", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Suite / Address 2
                </label>
                <input
                  type="text"
                  value={data.customer.address2}
                  onChange={(e) => setCustomerField("address2", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">City</label>
                <input
                  type="text"
                  value={data.customer.city}
                  onChange={(e) => setCustomerField("city", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium">State</label>
                  <input
                    type="text"
                    value={data.customer.state}
                    onChange={(e) => setCustomerField("state", e.target.value)}
                    className="border-hpi-med-gray w-full rounded border px-3 py-2"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Zip</label>
                  <input
                    type="text"
                    value={data.customer.zip}
                    onChange={(e) => setCustomerField("zip", e.target.value)}
                    className="border-hpi-med-gray w-full rounded border px-3 py-2"
                    maxLength={10}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Office Phone
                </label>
                <input
                  type="tel"
                  value={data.customer.office_phone}
                  onChange={(e) =>
                    setCustomerField("office_phone", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Email</label>
                <input
                  type="email"
                  value={data.customer.email}
                  onChange={(e) => setCustomerField("email", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Website</label>
                <input
                  type="url"
                  value={data.customer.website}
                  onChange={(e) => setCustomerField("website", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium">Notes</label>
                <textarea
                  value={data.customer.notes}
                  onChange={(e) => setCustomerField("notes", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  rows={3}
                />
              </div>
            </div>
          </fieldset>

          {/* Contact Section */}
          <fieldset className="border-hpi-light-gray space-y-4 rounded border p-4">
            <legend className="text-hpi-dark-blue px-2 font-semibold">
              Contact
            </legend>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium">Prefix</label>
                <select
                  value={data.contact.salutation}
                  onChange={(e) =>
                    setContactField("salutation", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                >
                  <option value="">Select...</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Ms">Ms</option>
                  <option value="Dr">Dr</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium">
                  First Name <span className="text-hpi-red">*</span>
                </label>
                <input
                  type="text"
                  value={data.contact.first_name}
                  onChange={(e) =>
                    setContactField("first_name", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                  required
                />
                {errors?.first_name && (
                  <p className="text-hpi-red text-sm">
                    {errors.first_name.join(", ")}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium">Middle Name</label>
                <input
                  type="text"
                  value={data.contact.middle_name}
                  onChange={(e) =>
                    setContactField("middle_name", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Last Name</label>
                <input
                  type="text"
                  value={data.contact.last_name}
                  onChange={(e) => setContactField("last_name", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Suffix</label>
                <input
                  type="text"
                  value={data.contact.suffix}
                  onChange={(e) => setContactField("suffix", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={5}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Title / Department
                </label>
                <input
                  type="text"
                  value={data.contact.department}
                  onChange={(e) =>
                    setContactField("department", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Role</label>
                <select
                  value={data.contact.role}
                  onChange={(e) => setContactField("role", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                >
                  <option value="">Select...</option>
                  {contact_roles.map((cr) => (
                    <option key={cr.id} value={cr.id}>
                      {cr.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Office Phone
                </label>
                <input
                  type="tel"
                  value={data.contact.phone}
                  onChange={(e) => setContactField("phone", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Cell Phone</label>
                <input
                  type="tel"
                  value={data.contact.cell_phone}
                  onChange={(e) =>
                    setContactField("cell_phone", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Email</label>
                <input
                  type="email"
                  value={data.contact.email}
                  onChange={(e) => setContactField("email", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
              </div>
            </div>
          </fieldset>

          {/* Next Action (Optional) */}
          <fieldset className="border-hpi-light-gray space-y-4 rounded border p-4">
            <legend className="text-hpi-dark-blue px-2 font-semibold">
              Next Action{" "}
              <span className="text-hpi-med-gray text-sm font-normal">
                (Optional)
              </span>
            </legend>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium">When</label>
                <input
                  type="date"
                  value={data.follow_up.scheduled_date}
                  onChange={(e) =>
                    setFollowUpField("scheduled_date", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">How</label>
                <select
                  value={data.follow_up.log_type_id}
                  onChange={(e) =>
                    setFollowUpField("log_type_id", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                >
                  <option value="">Select...</option>
                  {log_types.map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      {lt.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium">Assign to</label>
                <select
                  value={data.follow_up.user_id}
                  onChange={(e) => setFollowUpField("user_id", e.target.value)}
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                >
                  <option value="">Manager</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium">
                  Describe what needs to be done
                </label>
                <textarea
                  value={data.follow_up.scheduled_notes}
                  onChange={(e) =>
                    setFollowUpField("scheduled_notes", e.target.value)
                  }
                  className="border-hpi-med-gray w-full rounded border px-3 py-2"
                  rows={3}
                />
              </div>
            </div>
          </fieldset>

          {/* Actions */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              {processing ? "Saving..." : "Add Customer & Contact"}
            </button>
            <Link
              href={`/marketing_activities/${activity.id}/list_items`}
              className="hover:bg-hpi-pale-gray rounded border px-4 py-2"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
